import type {
  BackendStatus,
  Conversation,
  ConversationWithMessages,
  ConversationsListResponse,
  HealthResponse,
  ModelsResponse,
  ToolInfo,
  ClientMessage,
  ServerMessage,
} from '../types';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizeHttpBaseUrl(value?: string): string | null {
  if (!value) {
    return null;
  }

  const trimmed = trimTrailingSlash(value.trim());
  if (!trimmed) {
    return null;
  }

  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

function normalizeWsBaseUrl(value?: string): string | null {
  if (!value) {
    return null;
  }

  const trimmed = trimTrailingSlash(value.trim());
  if (!trimmed) {
    return null;
  }

  return trimmed.endsWith('/ws') ? trimmed : `${trimmed}/ws`;
}

const API_BASE = normalizeHttpBaseUrl(import.meta.env.VITE_API_URL) ?? '/api';

const WS_BASE =
  normalizeWsBaseUrl(import.meta.env.VITE_WS_URL) ??
  (() => {
    const configuredApiBase = normalizeHttpBaseUrl(import.meta.env.VITE_API_URL);
    if (configuredApiBase) {
      const wsOrigin = configuredApiBase
        .replace(/^http:\/\//, 'ws://')
        .replace(/^https:\/\//, 'wss://');
      return wsOrigin.replace(/\/api$/, '/ws');
    }

    return '/ws';
  })();

function isAbsoluteUrl(value: string): boolean {
  return /^wss?:\/\//.test(value) || /^https?:\/\//.test(value);
}

const SESSION_KEY = 'binks_session_id';

export function getBinksSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// Fetch wrapper with error handling
async function fetchAPI<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Binks-Session': getBinksSessionId(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Health check
export async function getHealth(): Promise<HealthResponse> {
  return fetchAPI<HealthResponse>('/health');
}

// Conversations
export async function listConversations(
  limit?: number,
  offset?: number
): Promise<ConversationsListResponse> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (offset) params.set('offset', offset.toString());
  const query = params.toString();
  return fetchAPI<ConversationsListResponse>(`/conversations${query ? `?${query}` : ''}`);
}

export async function createConversation(
  title?: string,
  systemPrompt?: string
): Promise<Conversation> {
  return fetchAPI<Conversation>('/conversations', {
    method: 'POST',
    body: JSON.stringify({ title, system_prompt: systemPrompt }),
  });
}

export async function getConversation(id: string): Promise<ConversationWithMessages> {
  return fetchAPI<ConversationWithMessages>(`/conversations/${id}`);
}

export async function updateConversation(
  id: string,
  updates: { title?: string; system_prompt?: string }
): Promise<Conversation> {
  return fetchAPI<Conversation>(`/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteConversation(id: string): Promise<void> {
  await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'DELETE',
    headers: { 'X-Binks-Session': getBinksSessionId() },
  });
}

// Tools
export async function listTools(): Promise<ToolInfo[]> {
  return fetchAPI<ToolInfo[]>('/tools');
}

// Models
export async function listModels(): Promise<ModelsResponse> {
  return fetchAPI<ModelsResponse>('/models');
}

export async function getBackendStatus(): Promise<BackendStatus> {
  let health: HealthResponse;

  try {
    health = await getHealth();
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown backend error';
    return {
      state: 'backend_unreachable',
      message: 'Backend unreachable',
      detail,
    };
  }

  try {
    await listModels();
    return {
      state: 'ready',
      message: `Backend ready: ${health.model}`,
      detail: `${health.gateway_type} ${health.gateway_url}`,
      health,
    };
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unknown model error';
    return {
      state: 'models_unavailable',
      message: 'Backend reachable, models unavailable',
      detail,
      health,
    };
  }
}

// WebSocket chat connection
export class ChatWebSocket {
  private ws: WebSocket | null = null;
  private conversationId: string;
  private onMessage: (msg: ServerMessage) => void;
  private onClose: () => void;
  private onError: (error: Event) => void;

  constructor(
    conversationId: string,
    onMessage: (msg: ServerMessage) => void,
    onClose: () => void,
    onError: (error: Event) => void
  ) {
    this.conversationId = conversationId;
    this.onMessage = onMessage;
    this.onClose = onClose;
    this.onError = onError;
  }

  connect(): void {
    const baseUrl = isAbsoluteUrl(WS_BASE)
      ? `${WS_BASE}/chat/${this.conversationId}`
      : (() => {
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const host = window.location.host;
          return `${protocol}//${host}${WS_BASE}/chat/${this.conversationId}`;
        })();
    const url = `${baseUrl}?session=${encodeURIComponent(getBinksSessionId())}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        this.onMessage(msg);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket closed');
      this.onClose();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.onError(error);
    };
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected');
    }
  }

  sendMessage(content: string, servers?: string[], model?: string): void {
    this.send({ type: 'message', content, servers, model });
  }

  sendCancel(): void {
    this.send({ type: 'cancel' });
  }

  sendPing(): void {
    this.send({ type: 'ping' });
  }

  close(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
